import { Api, HorizontalAlignment, LogManager, Model, ModelRow, StringUtil } from "@mcleod/core";
import { Label, Layout, LookupModelSelectionEvent, MainPageAccessor, Overlay, Panel, Snackbar, TableRow, Toast } from "@mcleod/components";
import { LMEGeneralSettings } from "@mcleod/general";
import { AutogenLayoutUnassignCarrierPrompt } from "./autogen/AutogenLayoutUnassignCarrierPrompt";
import { CommonDialogs } from "@mcleod/common";
import { ModifyCarrierPay } from "@mcleod/powerbroker";
import { MultiSelectRemoveCarrier } from "./MultiSelectRemoveCarrier";
import { DataFusionUtil } from "@mcleod/datafusion";

const log = LogManager.getLogger("lme.dispatch.UnassignCarrierPrompt");
export class UnassignCarrierPrompt extends AutogenLayoutUnassignCarrierPrompt {
    private _movementId: string;
    private _tonuCharges: Array<ModelRow>;
    brTrackingVendor: string;
    private reasonRequired: boolean;
    private _overlay: Overlay;
    success: boolean;
    private unassignSnackbar: Snackbar;
    public toastMessage: string;
    onCancel: () => void;
    onSave: (success: boolean, vendorName: string, forceCancel: boolean, layout: UnassignCarrierPrompt) => Promise<void>;

    override async onLoad(): Promise<void> {
        this.reasonRequired = "Y" === LMEGeneralSettings.getSingleton().getDispatchControl().unassign_reason;
        this.textboxReasonCode.required = this.textboxReasonCode.visible = this.reasonRequired;
        this.textboxComments.required = this.textboxComments.visible = this.reasonRequired;
        if (this.reasonRequired) {
            this.textboxReasonCode.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
                if (event.hasSelection() === true)
                    event.target.text = event.selection?.get("descr");
            });
            this.textboxReasonCode.addBeforeLookupModelSearchListener(event => {
                event.filter.is_active = "Y";
                event.filter.code_type = "U";
            });
        }

        if (this._movementId) {
            //Search for carriers with advanced already listed for this movement, and list those carries if found
            return Api.search("lme/dispatch/carriers-with-advances", { movement_id: this.movementId })
                .then(resp => {
                    const data = resp.data?.[0]
                    if (data?.carriers && data.carriers.length > 0) {
                        this.advanceCarriersLabel.visible = true
                        this.advanceCarriersLabel.text =
                            `Advance(s) issued to:\n ${data.carriers.join(", ")}`
                    }
                })
        }
    }

    async unassignCarrier() {
        this.unassignSnackbar = Snackbar.showDownloadSnackbar("Unassign Carrier", "Unassigning...", {
            targetPanel: MainPageAccessor.router
        });
        return Api.post("lme/dispatch/cancel-dispatch", {
            movement_ids: [this.movementId],
            reason_code: this.textboxReasonCode.getFirstLookupModelData()?.get('id'),
            comment: this.textboxComments.text
        }, null, null).then(async response => {

            const status = response?.data?.[0]
            this.success = status?.success;

            if (this.brTrackingVendor) {
                await this.cancelTracking(this.movementId);
            } else if (this.onSave) {
                await this.onSave(this.success, "", false, this);
            }
        }).finally(() => {
            this.unassignSnackbar.dismiss();
            if (!StringUtil.isEmptyString(this.toastMessage) && this.success) {
                Toast.showSuccessToast(this.toastMessage);
            }
            else if (!this.success) {
                CommonDialogs.showError("Carrier unassignment failed");
            }
        });
    }

    async cancelTracking(movementId: string) {
        return await Api.post("lme/powerbroker/tracking/cancel-tracking", { "movement_ids": [movementId] })
            .then(response => {
                const status = response?.data?.[0];
                this.onSave(this.success, status.vendor_name, status.force_cancel, this);
            });
    }

    get movementId(): string {

        return this._movementId;

    }
    set movementId(value: string) {

        this._movementId = value;

    }

    get tonuCharges(): Array<ModelRow> {
        return this._tonuCharges;
    }

    set tonuCharges(value: Array<ModelRow>) {
        this._tonuCharges = value;
    }

    static async showPrompt(moveRow: ModelRow, tonu_earning_code?: String, onSave?: (success: boolean, vendorName: string, forceCancel: boolean, layout: UnassignCarrierPrompt) => Promise<void>): Promise<void> {
        const moveId = moveRow?.getString("id", moveRow?.getString("movement_id", null));

        if (moveId != null) {
            const unassignCarrierPrompt = Layout.getLayout("lme/dispatch/UnassignCarrierPrompt") as UnassignCarrierPrompt;

            unassignCarrierPrompt.movementId = moveId;
            if (!moveRow.isNull("br_vendor")) {
                unassignCarrierPrompt.brTrackingVendor = moveRow.getString("br_vendor");
            }

            if (tonu_earning_code) {
                unassignCarrierPrompt.addLayoutLoadListener(async () => {
                    const otherCharges = await unassignCarrierPrompt.sourceExtraPay.search({ "movement_id": moveId });
                    const tonuCharges: Array<ModelRow> = otherCharges.modelRows.filter((row: ModelRow) => row.get("deduct_code_id") == tonu_earning_code)
                    unassignCarrierPrompt.tonuCharges = tonuCharges;
                });
            }

            unassignCarrierPrompt.onSave = onSave;

            const carrierId = moveRow.get("override_payee_id", null);
            await CommonDialogs.showYesNo(unassignCarrierPrompt, "Remove Carrier", {
                yesButtonProps: {
                    onClick: async () => {
                        if (unassignCarrierPrompt.validateSimple()) {
                            if (carrierId && !moveRow.get("is_brokerage_ltl")) {
                                this.showTONUYesNO(carrierId, unassignCarrierPrompt);
                            } else {
                                unassignCarrierPrompt.unassignCarrier();
                            }
                        }
                    }
                },
                movable: true
            });
        }
    }

    static showMultiPrompt(selectedRows: TableRow[]) {
        const isMulti = selectedRows.length > 1;
        const unassignCarrierPrompt = Layout.getLayout("lme/dispatch/UnassignCarrierPrompt") as UnassignCarrierPrompt;
        unassignCarrierPrompt.addLayoutLoadListener(() => {
            unassignCarrierPrompt.labelSingle.visible = !isMulti;
            unassignCarrierPrompt.labelMulti.visible = isMulti;
        });
        let reason, reasonCode;
        CommonDialogs.showYesNo(unassignCarrierPrompt, "Remove Carrier(s)", {
            yesButtonProps: {
                onClick: async () => {
                    if (unassignCarrierPrompt.validateSimple()) {
                        reasonCode = unassignCarrierPrompt.textboxReasonCode.getFirstLookupModelData()?.get('id');
                        reason = unassignCarrierPrompt.textboxComments.text;
                    }
                }
            },
            movable: true
        }).then(confirmed => {
            if (confirmed && isMulti) {
                MultiSelectRemoveCarrier.showSlideout(selectedRows, reasonCode, reason);
            } else if (confirmed) {
                const moveRow = selectedRows[0].data;
                const carrierId = moveRow.get("carrier_id", null);
                const moveId = moveRow.getString("movement_id");
                unassignCarrierPrompt.movementId = moveId;
                unassignCarrierPrompt.onSave = async (success: boolean, vendorName: string, forceCancel: boolean, unassignLayout: UnassignCarrierPrompt) => {
                    DataFusionUtil.isCancelAllowed(carrierId, moveId).then(cancelAllowed => {
                        if (cancelAllowed) {
                            DataFusionUtil.sendCancelTender(carrierId, moveId, true);
                        }
                    });
                }

                if (carrierId && !moveRow.get("is_brokerage_ltl")) {
                    this.showTONUYesNO(carrierId, unassignCarrierPrompt).finally(() => {
                        if (selectedRows.length > 1) {
                            selectedRows[0].table.selectedRows = [];
                        }
                    });
                } else {
                    unassignCarrierPrompt.unassignCarrier().finally(() => {
                        if (selectedRows.length > 1) {
                            selectedRows[0].table.selectedRows = [];
                        }
                    });
                }
            }
        });
    }

    static async showTONUYesNO(carrierId: string, unassignCarrierPromptLayout: UnassignCarrierPrompt) {
        const pcData = await Model.searchSingleRecord("lme/dispatch/payroll-control")
        const okClicked = await CommonDialogs.showYesNo(`Would you like to pay the carrier for a TONU?`, "TONU Pay")
        if (okClicked) {
            this.openTONUSlideout(pcData.data?.tonu_earning_code, carrierId, unassignCarrierPromptLayout);
            return;
        }
        return unassignCarrierPromptLayout.unassignCarrier();
    }

    static openTONUSlideout(tonu_earning_code: String, carrierId: string, unassignCarrierPromptLayout: UnassignCarrierPrompt) {
        const moveId = unassignCarrierPromptLayout.movementId;
        ModifyCarrierPay.showSlideout(moveId, carrierId, async (movementSaved: boolean, moveRow: ModelRow, extraPayRows: ModelRow[]) => {
            await unassignCarrierPromptLayout.unassignCarrier();
            this.addTONUCharges(extraPayRows, moveRow.get("id"));
        }, tonu_earning_code);
    }

    static addTONUCharges(tonuCharges: Array<ModelRow>, movementID: String) {
        tonuCharges.forEach(async (row: ModelRow) => {
            const modelRow = new ModelRow("lme/powerbroker/extra-pay", row._appending);
            modelRow.setValues(row.data);
            modelRow.set("movement_id", movementID);
            await Api.update("lme/powerbroker/extra-pay", modelRow);
        })
    }
}
